import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { createFormGroupFromObject } from '@applications/sha-services';
import {
  Address,
  Associate,
  Employee,
  MessageType,
  ProvinceState,
  ServiceAppointment,
  ServiceCode,
  TechnicianGroup,
  TransportOption,
  Vehicle,
} from '@quorum/models/xs-resource';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';

@Component({
  selector: 'svc-appointment-confirmation-dialog',
  templateUrl: './appointment-confirmation-dialog.component.html',
  styleUrls: ['./appointment-confirmation-dialog.component.scss'],
})
export class AppointmentConfirmationDialogComponent implements OnInit {
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  numberMask = createNumberMask({
    prefix: '',
    suffix: '',
  });
  odometerInSub$: Subscription;
  linkedServiceVisitSub$: Subscription;
  overrideMessagePreferenceSub$: Subscription;
  messagePreferenceSub$: Subscription;
  destroy$ = new Subject();
  contactInfoPlaceholderText = 'Contact Info';
  formSubmitted = false;
  odometerEdited = false;
  isSaviOdometer = false;

  verificationForm = this.formBuilder.group({
    customerAssociate: createFormGroupFromObject(this.dialogData.customerAssociate),
    address: createFormGroupFromObject(this.dialogData.address),
    appointment: createFormGroupFromObject(this.dialogData.appointment),
    overrideOptions: this.formBuilder.group({
      excludeCommunicator: [this.dialogData.appointment.communicatorExcludeFrom],
      messagePreference: [null],
      messagePreferenceContactInfo: [null],
    }),
  });

  constructor(
    public dialogRef: MatDialogRef<AppointmentConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      address: Address;
      appointment: ServiceAppointment;
      customerAssociate: Associate;
      serviceAppointmentDropdowns: {
        employees: Employee[];
        technicianGroups: TechnicianGroup[];
        serviceAdvisors: Employee[];
        serviceCodes: ServiceCode[];
        technicians: Employee[];
        messageTypes: MessageType[];
        transportOptions: TransportOption[];
        provinceStates: ProvinceState[];
      };
      vehicle: Vehicle;
      vehicleDiagnostics: { enabled: boolean; dealerCountryCode: string };
      oem: string;
    },
    private formBuilder: FormBuilder,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade
  ) {}

  ngOnInit() {
    const odometerControl = this.verificationForm.get('appointment.odometerIn');

    odometerControl.setValidators([Validators.required]);
    this.verificationForm.get('appointment.serviceAdvisorId').setValidators([Validators.required]);

    this.isSaviOdometer =
      this.dialogData.vehicleDiagnostics?.enabled &&
      this.dialogData.oem === 'GM' &&
      this.dialogData.appointment.odometerUpdatedByVehicleDiagnostic === true;

    this.odometerInSub$ = odometerControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (odometerControl.dirty) {
        this.odometerEdited = true;
        this.isSaviOdometer = false;
        this.verificationForm
          .get('appointment.odometerUpdatedByVehicleDiagnostic')
          ?.setValue(false, { emitEvent: false });
      }
    });

    this.linkedServiceVisitSub$ = this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$
      .pipe(
        filter(
          (serviceVisit) =>
            this.dialogData.vehicleDiagnostics?.enabled &&
            this.dialogData.oem === 'GM' &&
            !!serviceVisit &&
            serviceVisit.vin === this.dialogData.vehicle?.vin &&
            this.svcVehicleDiagnosticsRootFacade.isWithinADay(serviceVisit.checkInDate, serviceVisit.checkInTime)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((serviceVisit) => {
        const saviOdometer = parseInt(serviceVisit.odometer, 10);

        this.verificationForm
          .get('appointment.serviceVisitId')
          ?.setValue(serviceVisit.serviceVisitId, { emitEvent: false });

        if (!odometerControl.dirty && !Number.isNaN(saviOdometer)) {
          odometerControl.setValue(saviOdometer, { emitEvent: false });
          this.verificationForm
            .get('appointment.odometerUpdatedByVehicleDiagnostic')
            ?.setValue(true, { emitEvent: false });
          this.isSaviOdometer = true;
        } else if (odometerControl.dirty) {
          this.verificationForm
            .get('appointment.odometerUpdatedByVehicleDiagnostic')
            ?.setValue(false, { emitEvent: false });
        }
      });

    /**
     * XS-5013: If the appointment has no license plate number, but the vehicle does, use vehicle value
     */
    if (!this.verificationForm.get('appointment.licenseNumber').value && this.dialogData.vehicle?.licensePlateNumber) {
      this.verificationForm.get('appointment.licenseNumber').setValue(this.dialogData.vehicle?.licensePlateNumber);
    }

    /**
     * If the customer associate message preference is set to anything but None/null, clear the errors that may be set
     * Had to do this to clear the red outline on the overrideOptions message preference field
     */
    this.messagePreferenceSub$ = this.verificationForm
      .get('customerAssociate.messagePreferenceId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((messagePreferenceId: number) => {
        if (messagePreferenceId) {
          if (![null, 4].includes(messagePreferenceId)) {
            this.verificationForm.get('overrideOptions.messagePreference').setErrors(null);
          }

          switch (messagePreferenceId) {
            case 1:
              this.verificationForm.get('address.cellPhoneNumber').setValidators(null);
              this.verificationForm.get('address.cellPhoneNumber').updateValueAndValidity({ emitEvent: false });
              break;
            case 2:
            case 3:
              this.verificationForm.get('customerAssociate.emailAddress').setValidators(null);
              this.verificationForm.get('customerAssociate.emailAddress').updateValueAndValidity({ emitEvent: false });
              break;
            case 4:
              this.verificationForm.get('address.cellPhoneNumber').setValidators(null);
              this.verificationForm.get('address.cellPhoneNumber').updateValueAndValidity({ emitEvent: false });
              this.verificationForm.get('customerAssociate.emailAddress').setValidators(null);
              this.verificationForm.get('customerAssociate.emailAddress').updateValueAndValidity({ emitEvent: false });
              break;
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNestedFormGroup(formGroupName: string): FormGroup {
    return this.verificationForm.get(formGroupName) as FormGroup;
  }

  verifyCustomer(verificationData: any) {
    this.formSubmitted = true;
    const customerAssociate = verificationData.customerAssociate;
    const address = verificationData.address;
    let appointment = verificationData.appointment;
    const overrideOptions = verificationData.overrideOptions;

    this.verificationForm.updateValueAndValidity();

    // Ensure email or cell phone are set if the corresponding message type is selected
    if (customerAssociate.messagePreferenceId) {
      if (customerAssociate.messagePreferenceId === 1) {
        this.verificationForm
          .get('customerAssociate.emailAddress')
          .setValidators([Validators.email, Validators.required]);
        this.verificationForm.get('customerAssociate.emailAddress').updateValueAndValidity({ emitEvent: false });
      }

      if (customerAssociate.messagePreferenceId === 2 || customerAssociate.messagePreferenceId === 3) {
        if (!address.cellPhoneNumber) {
          this.verificationForm.get('address.cellPhoneNumber').setValidators([Validators.required]);
          this.verificationForm.get('address.cellPhoneNumber').updateValueAndValidity({ emitEvent: false });
        } else {
          const strippedPhoneNumber = address.cellPhoneNumber?.replace(/\D+/g, '');
          if (strippedPhoneNumber.length < 10) {
            this.verificationForm.get('address.cellPhoneNumber').setErrors({ invalidPhoneNumber: true });
          }
        }
      }
    }

    // Ensure customer associate message preference is set to something other than None/null before override can be configured
    if (
      [null, 4].includes(customerAssociate.messagePreferenceId) &&
      [1, 2, 3].includes(overrideOptions.messagePreference)
    ) {
      this.verificationForm.get('overrideOptions.messagePreference').setErrors({ messagePreferenceRequired: true });
    }

    if (typeof appointment.odometerIn === 'string') {
      const unmaskedOdometer = appointment.odometerIn ? parseInt(appointment.odometerIn.replaceAll(',', '')) : null;
      appointment = { ...appointment, odometerIn: unmaskedOdometer };
    }

    // Ensure entered odometer is not less than the current odometer reading of the vehicle

    if (
      appointment.odometerIn &&
      appointment.odometerIn !== '' &&
      appointment.odometerIn < this.dialogData.vehicle.currentMileage
    ) {
      this.verificationForm
        .get('appointment.odometerIn')
        .setErrors({ invalidOdometer: this.dialogData.vehicle.currentMileage });
    }

    // Ensure that the priority code corresponds to the selected service code.
    if (appointment?.serviceCode) {
      const serviceCodes = this.dialogData.serviceAppointmentDropdowns.serviceCodes;

      const serviceCode = serviceCodes.find((sc) => sc.serviceCode === appointment.serviceCode);
      if (serviceCode) {
        appointment.priorityNumber = serviceCode.priorityNumber;
      }
    }

    this.verificationForm.markAllAsTouched();

    if (this.verificationForm.valid) {
      this.dialogRef.close({
        customerAssociate,
        address,
        appointment,
        overrideOptions,
        odometerEdited: this.odometerEdited,
      });
    }
  }

  skipVerifyCustomer() {
    this.dialogRef.close();
  }

  changeContactInfoPlaceholder(messageTypePreferenceId: number, messageTypes: MessageType[]) {
    const messageType = new MessageType({
      ...messageTypes.find((mt) => mt.id === messageTypePreferenceId),
    });
    switch (messageType.description) {
      case 'Email':
        this.contactInfoPlaceholderText = 'Email';
        break;
      case 'Text':
        this.contactInfoPlaceholderText = 'Cell Phone';
        break;
      default:
        this.contactInfoPlaceholderText = 'Contact Information';
    }
  }
}
